import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Gear from 'plaid-threads/Icons/Gear';

// components
import { Button, IconButton, Checkbox } from 'src/components/Inputs';
import { Card, usePaywallProvider } from 'src/components/Surfaces';

// helpers
import { useOutsideClick } from 'src/hooks';
import { NEXT_PUBLIC_IS_PREVIEW } from 'src/env';
import { isDevelopment } from 'src/lib/constants';

import styles from './ContentfulToolbar.module.scss';

const ContentfulToolbar = () => {
  const { isPreview } = useRouter();
  const paywallContext = usePaywallProvider();
  // The preview service sets NEXT_PUBLIC_IS_PREVIEW. Local Contentful preview
  // sets Next's preview cookie. `next dev` is NODE_ENV development even when
  // the shell has PLAID_ENV=testing, which is how local preview is usually run.
  const showSettings =
    Boolean(NEXT_PUBLIC_IS_PREVIEW) ||
    isPreview ||
    isDevelopment ||
    process.env.NODE_ENV === 'development';
  const [state, setState] = useState({
    isOpen: false,
    settings: {},
  });

  const toggleSettings = () => {
    setState((state) => {
      return { ...state, isOpen: !state.isOpen };
    });
  };
  const outsideClickRef = useOutsideClick(toggleSettings);

  const handleCheckbox = (e) => {
    setState((state) => {
      return {
        ...state,
        settings: {
          ...state.settings,
          [e.target.name]: e.target.checked,
        },
      };
    });
  };

  const applyChanges = () => {
    const { settings } = state;
    if (settings?.resetPaywall) {
      paywallContext?.resetPaywall();
    }
    if (settings?.clearPreview) {
      window.location.replace('/api/clear-preview-mode-cookies');
    }
    setState((state) => {
      return { ...state, isOpen: false };
    });
  };

  if (showSettings) {
    return (
      <>
        <IconButton
          className={styles.settingsBtn}
          accessibilityLabel='Contentful Settings'
          icon={<Gear />}
          onClick={toggleSettings}
        />
        {state.isOpen && (
          <div className={styles.cardWrapper} ref={outsideClickRef}>
            <Card
              className={styles.previewCard}
              leadingObject={<h4>Contentful Settings</h4>}
              body={
                <>
                  <Checkbox
                    id='clearPreview'
                    name='clearPreview'
                    onChange={(e) => {
                      return handleCheckbox(e);
                    }}
                    value={state.settings?.['clearPreview'] || false}
                  >
                    Clear Preview
                  </Checkbox>
                  {paywallContext.canResetPaywall && (
                    <Checkbox
                      id='resetPaywall'
                      name='resetPaywall'
                      value={state.settings?.['resetPaywall'] || false}
                      onChange={(e) => {
                        return handleCheckbox(e);
                      }}
                    >
                      Reset Paywall
                    </Checkbox>
                  )}
                </>
              }
              footer={
                <div className={styles.buttonGroup}>
                  <Button
                    className={styles.closeBtn}
                    onClick={toggleSettings}
                    secondary
                    small
                  >
                    Close
                  </Button>
                  <Button
                    className={styles.applyBtn}
                    onClick={applyChanges}
                    primary
                    small
                  >
                    Apply
                  </Button>
                </div>
              }
            />
          </div>
        )}
      </>
    );
  }

  return null;
};

export default ContentfulToolbar;
